// UKOffers — GBP currency utilities (UK format: £1,000.50)

export const formatGBP = (price, priceType) => {
  if (price === null || price === undefined) return 'Price on request';
  if (price === 0 && !priceType) return 'Free';

  // British number format: £1,000.50
  const formatted = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(price);

  const suffixMap = {
    'daily': '/day', 'Daily': '/day',
    'monthly': '/month', 'Monthly': '/month',
    'hourly': '/hour', 'Hourly': '/hour',
    'weekly': '/week', 'Weekly': '/week',
    'yearly': '/year', 'Yearly': '/year', 'annually': '/year',
    'package': '/package', 'paket': '/package',
  };
  const suffix = suffixMap[priceType] || '';

  return `£${formatted}${suffix}`;
};

// Backward compatibility aliases
export const formatTL = formatGBP;
export const formatPrice = formatGBP;

/**
 * Returns CSS class based on price.
 * If price is missing, smaller and grey; otherwise large and orange.
 */
export const priceClassName = (price, specifiedClass = 'font-bold text-[#f97316] text-lg', unspecifiedClass = 'text-xs text-slate-400 font-medium') => {
  return (price === null || price === undefined) ? unspecifiedClass : specifiedClass;
};

// General number formatting with thousands separator (UK style)
export const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('en-GB').format(num);
};

// Input number formatter — adds thousand separators while typing
export const formatNumberInput = (value) => {
  const numericValue = value.toString().replace(/[^\d]/g, '');
  if (!numericValue) return '';
  return new Intl.NumberFormat('en-GB').format(parseInt(numericValue, 10));
};

// Strip formatting from a number input
export const parseNumberInput = (formattedValue) => {
  if (!formattedValue) return '';
  // UK format uses comma as thousands separator
  return formattedValue.toString().replace(/,/g, '');
};

// Price input formatter
export const formatPriceInput = (value) => {
  const numericValue = value.replace(/[^\d]/g, '');
  if (!numericValue) return '';
  return new Intl.NumberFormat('en-GB').format(parseInt(numericValue, 10));
};

// Strip formatting from a price input
export const parsePriceInput = (formattedValue) => {
  if (!formattedValue) return '';
  return formattedValue.replace(/,/g, '');
};

/**
 * Min/Max validation
 */
export const validateMinMax = (values, field, newValue) => {
  const minVal = field === 'min' ? parseFloat(parseNumberInput(newValue) || 0) : parseFloat(parseNumberInput(values.min) || 0);
  const maxVal = field === 'max' ? parseFloat(parseNumberInput(newValue) || 0) : parseFloat(parseNumberInput(values.max) || 0);

  let error = null;
  let correctedMin = minVal;
  let correctedMax = maxVal;

  if (minVal > 0 && maxVal > 0) {
    if (field === 'min' && minVal > maxVal) {
      correctedMin = maxVal;
      error = 'Minimum value cannot be greater than maximum';
    } else if (field === 'max' && maxVal < minVal) {
      correctedMax = minVal;
      error = 'Maximum value cannot be less than minimum';
    }
  }

  return {
    min: correctedMin > 0 ? formatNumberInput(correctedMin.toString()) : '',
    max: correctedMax > 0 ? formatNumberInput(correctedMax.toString()) : '',
    error
  };
};

// Date formatting (en-GB)
export const formatDate = (dateStr, options = {}) => {
  if (!dateStr) return '-';
  const defaultOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...options
  };
  return new Date(dateStr).toLocaleDateString('en-GB', defaultOptions);
};

// Relative time formatting (English)
export const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;

  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)} d ago`;

  return formatDate(dateStr);
};
